import React, { useEffect, useState } from "react";
import { NavLink, useNavigate, useLocation } from "react-router-dom";
import { Col, Row } from "reactstrap";
import { Table, Tabs, Empty, Tooltip } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { Mail, MessageCircle, Send } from "lucide-react";
import { getBroadCasts, getCampaignById } from "../../services/broadcast";
import Header from "../../components/header";
import moment from "moment";
import { toast } from "react-hot-toast";

import { useSelector } from "react-redux";
import CampaignView from './campaignView';

import type { CampaignFormData, CampaignViewProps, LeadStatusType, } from './campaignViewTypes';

import "./whatsappBroadcast.css";
import UltimatePlanInfo from "../../components/UltimatePlanInfo/UltimatePlanInfo";
import LimitsDisplay from "./LimitsDisplay";
import MetricsDisplay from "./MetricsDisplay";
import logger from "../../utils/logger";

// Add this interface after imports
interface DailyMailTracker {
  type: string;
  isTodayLimitReached: boolean;
  allowedLimit: number;
  todayRemainingLimit: number;
}




interface CampaignItem {
  key: string;
  id: string;
  name: string;
  scheduleAt: string;
  rawScheduleAt?: string;
  totalQueued: number;
  totalProcessed: number;
  type: string;
  createdBy: string;
  status: string;
}


interface Lead {
  _id: string;
  key: string;
  name: string;
  status: string[];
  lastActivity: string;
  phone?: string;
  email?: string;
  lastActivityType?: string;
}



const EmailLimitsDisplay = ({ dailyMailTracker }: { dailyMailTracker: DailyMailTracker[] }) => {
  logger.log('📧 Email limits data:', dailyMailTracker);
  
  // Calculate total remaining and total limits across all email services
  const totalRemaining = dailyMailTracker?.reduce((sum: number, tracker: DailyMailTracker) =>
    sum + tracker.todayRemainingLimit, 0) || 0;
  const totalLimit = dailyMailTracker?.reduce((sum: number, tracker: DailyMailTracker) =>
    sum + tracker.allowedLimit, 0) || 0;

  // Calculate usage percentage and determine status class
  const usagePercentage = totalLimit > 0 ? ((totalLimit - totalRemaining) / totalLimit) * 100 : 0;
  const isExceeded = totalRemaining === 0 || usagePercentage >= 100;
  const isWarning = !isExceeded && usagePercentage >= 80;

  const limitStatusClass = isExceeded ? 'exceeded' : isWarning ? 'warning' : '';

  // Create detailed tooltip content showing each service breakdown
  const tooltipContent = (
    <div style={{ padding: '8px 0' }}>
      <div style={{ 
        fontWeight: 600, 
        marginBottom: '8px', 
        color: '#fff',
        fontSize: '14px'
      }}>
        📧 Daily Email Limits
      </div>
      
      {dailyMailTracker?.map((tracker: DailyMailTracker, index: number) => (
        <div 
          key={tracker.type} 
          style={{ 
            display: 'flex', 
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: '6px 0',
            color: '#fff',
            borderBottom: index < dailyMailTracker.length - 1 ? '1px solid rgba(255,255,255,0.1)' : 'none'
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            {/* Service icon */}
            {tracker.type === 'gmail' && <img alt="Gmail" src="assets/img/gmail.png" style={{ width: 16, height: 16 }} />}
            {tracker.type === 'brevo' && <img alt="Brevo" src="assets/img/brevo.png" style={{ width: 16, height: 16 }} />}
            {tracker.type === 'zeptomail' && <img alt="Zeptomail" src="assets/img/email.png" style={{ width: 16, height: 16 }} />}
            
            <span style={{ fontSize: '13px', textTransform: 'capitalize' }}>
              {tracker.type}
            </span>
          </div>
          
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            {/* Status indicator dot */}
            <div
              style={{
                width: '8px',
                height: '8px',
                borderRadius: '50%',
                backgroundColor: tracker.isTodayLimitReached 
                  ? '#ff4d4f' 
                  : tracker.todayRemainingLimit < tracker.allowedLimit * 0.2
                    ? '#faad14'
                    : '#52c41a'
              }}
            />
            <span style={{ fontSize: '12px', fontWeight: 600 }}>
              {tracker.todayRemainingLimit}/{tracker.allowedLimit}
            </span>
          </div>
        </div>
      ))}
      
      <div style={{ 
        marginTop: '8px', 
        paddingTop: '8px', 
        borderTop: '1px solid rgba(255,255,255,0.1)',
        fontSize: '11px', 
        color: 'rgba(255,255,255,0.8)' 
      }}>
        📈 Format: Remaining/Total per day
      </div>
    </div>
  );

  return (
    <Tooltip 
      title={tooltipContent} 
      placement="bottomLeft" 
      color="#1f1f1f"
      overlayStyle={{ maxWidth: '280px' }}
    >
      <div className={`messaging-limit ${limitStatusClass}`} style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', flexWrap: 'wrap' }}>
        <Mail size={16} className="me-2" color={isExceeded ? "#dc3545" : isWarning ? "#856404" : "#1890ff"} />
        Email Limits: {totalRemaining}/{totalLimit} per day
      </div>
    </Tooltip>
  );
};


const { TabPane } = Tabs;
const PER_PAGE = 20;
const now = moment();

const WhatsappBroadcast: React.FC = () => {
  const [allCampaigns, setAllCampaigns] = useState<any[]>([]);
  const [apiTotal, setApiTotal] = useState(0);
  const [displayData, setDisplayData] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [isDetail, setIsDetail] = useState(false);
  const [formDetail, setFormDetail] = useState<any>({});
  const [messagingLimitTier, setMessagingLimitTier] = useState("");
  const [numericMessagingLimit, setNumericMessagingLimit] = useState("");
  const [activeTab, setActiveTab] = useState("current");
  const [isUltimate, setIsUltimate] = useState<boolean | null>(null);
const [dailyMailTracker, setDailyMailTracker] = useState<DailyMailTracker[]>([]);
 const [selectedCampaignId, setSelectedCampaignId] = useState<string | null>(null);
const [actionLoading, setActionLoading] = useState<string | null>(null);
const [sevenDayOrgStats, setSevenDayOrgStats] = useState<any>({});
const [thirtyDayOrgStats, setThirtyDayOrgStats] = useState<any>({});
const navigate = useNavigate();
const location = useLocation();


// Copy campaign handler
// Copy campaign handler
const handleCopyCampaign = async (campaign: any) => {
  try {
    setActionLoading(campaign.id); // Set loading state
    
    // Fetch full campaign details to get sender info
    const { data } = await getCampaignById(campaign.id, { page: 1, perPage: 1 });
    const fullCampaign = data.data;
    
    // Create copy data object
    const copyData = {
      name: `Copy of ${campaign.name}`,
      serviceType: campaign.type.toLowerCase(),
      // Only include sender details for email campaigns
      senderDetail: (campaign.type.toLowerCase() !== 'whatsapp' && fullCampaign.senderDetail) 
        ? fullCampaign.senderDetail 
        : null,
      leads: [], // Will be populated from original campaign  
      dailyLimit: fullCampaign.dailyLimit || campaign.totalQueued || 0,
      originalCampaignId: campaign.id,
      skipToStep: campaign.type.toLowerCase() === 'whatsapp' ? 2 : 1 // Skip logic
    };

    logger.log('📋 Copying campaign data:', copyData);

    // Store in localStorage for the creation page
    localStorage.setItem('copiedCampaignData', JSON.stringify(copyData));
    
    // Navigate to creation page
    navigate("/createWhatsappBroadcast?mode=copy");
    
  } catch (error) {
    console.error('❌ Error copying campaign:', error);
    // Fallback: still navigate but without sender details
    const copyData = {
      name: `Copy of ${campaign.name}`,
      serviceType: campaign.type.toLowerCase(),
      senderDetail: null,
      leads: [],
      dailyLimit: 0,
      originalCampaignId: campaign.id,
      skipToStep: 1
    };
    
    localStorage.setItem('copiedCampaignData', JSON.stringify(copyData));
    navigate("/createWhatsappBroadcast?mode=copy");
  } finally {
    setActionLoading(null); // Clear loading state
  }
};

  useEffect(() => {
    const local = window.localStorage?.getItem("userData");
    if (local) {
      const userData = JSON.parse(local);
      setIsUltimate(userData?.subscriptionPlanType?.toLowerCase() === "ultimate");
    }
  }, []);

  const { userDetails } = useSelector((state: any) => {
    return state?.rootReducers.userData;
  });

  const filterDataByTab = (data: any[], tab: string) => {
    const now = moment();
    return data.filter(item => {
      const scheduleTime = moment(item.scheduleAt, "Do MMM YY HH:mm A");
      return tab === "current" 
        ? scheduleTime.isSameOrBefore(now) 
        : scheduleTime.isAfter(now);
    });
  };



const columns: ColumnsType<any> = [
  {
    title: "Name",
    dataIndex: "name",
    key: "name",
    sorter: false,
    width: '20%',
    render: (text: string) => (
      <div className="broadcast_type_container">{text}</div>
    ),
  },
  {
    title: "Scheduled At",
    dataIndex: "scheduleAt",
    key: "scheduleAt",
    sorter: false,
    width: '15%',
  },
  {
    title: "Recipients",
    dataIndex: "totalQueued",
    key: "totalQueued",
    sorter: false,
    width: '10%',
    render: (value: number) => (
      <div className="text-center">{value}</div>
    ),
  },
  {
    title: "Sent to",
    dataIndex: "totalProcessed",
    key: "totalProcessed",
    sorter: false,
    width: '10%',
    render: (value: number) => (
      <div className="text-center">{value}</div>
    ),
  },
  {
    title: "Type",
    dataIndex: "type",
    key: "type",
    width: '15%',
    render: (_: any, record: any) => (
      <div className="broadcast_type_container">
        {record.type?.toLowerCase() === "whatsapp" ? (
          <img alt="WhatsApp" src="assets/img/whatsapp.png" />
        ) : record.type?.toLowerCase() === "gmail" ? (
          <img alt="Gmail" src="assets/img/gmail.png" />
        ) : record.type?.toLowerCase() === "brevo" ? (
          <img alt="Brevo" src="assets/img/brevo.png" />
        ) : record.type?.toLowerCase() === "zeptomail" ? (
          <img alt="Zeptomail" src="assets/img/email.png" /> // Use email icon for zeptomail or add specific icon
        ) : (
          <img alt="mail" src="assets/img/email.png" />
        )}
        {record.type}
      </div>
    ),
  },
  {
    title: "Created by",
    dataIndex: "createdBy",
    key: "createdBy",
    width: '15%',
    render: (_: any, record: any) => {
      const userName = userDetails?.organizationEmployee?.find(
        (e: any) => e._id === record.createdBy
      );
      return (
        <div className="broadcast_type_container">
          {userName?.firstName} {userName?.lastName ?? ""}
        </div>
      );
    },
  },
 {
  title: "Status",
  dataIndex: "status",
  key: "status",
  width: '15%',
  render: (status: string) => {
    const colorMap: { [key: string]: string } = {
      pending: "#F7CB73",
      cancelled: "#E32C2C",
      approved: "#4CB050",
      completed: "#3FAEFD",
      failed: "#FF4747",
      rejected: "#FF4747",
    };
    const color = colorMap[status?.toLowerCase()] || "#8c8c8c";
    
    return (
      <div
        className="broadcast_status_container"
        style={{ backgroundColor: color }}
      >
        {status}
      </div>
    );
  },
},
{
  title: "Actions",
  key: "actions",
  width: '10%',
  render: (_, record) => (
    <div style={{ display: 'flex', justifyContent: 'center' }}>
     <button
  onClick={(e) => {
    e.stopPropagation(); // Prevent row click
    handleCopyCampaign(record);
  }}
  className="campaign-copy-btn"
  title="Copy Campaign"
  disabled={actionLoading === record.id}
>
  {actionLoading === record.id ? (
    <i className="ri-loader-4-line copy-loading"></i>
  ) : (
    <i className="ri-file-copy-line"></i>
  )}
</button>
    </div>
  ),
},
];

const EmptyState = ({ activeTab }: { activeTab: string }) => (
  <div className="flex flex-col items-center justify-center py-12">
    <div className="mb-4">
      <MessageCircle className="w-12 h-12 text-gray-300" />
    </div>
    <Empty 
      image={Empty.PRESENTED_IMAGE_SIMPLE}
      description={
        <div className="text-gray-500">
          {activeTab === 'current' 
            ? "No current campaigns found" 
            : "No scheduled campaigns yet"}
        </div>
      }
    />
  </div>
);
  
  const fetchData = async (currentPage: number = 1) => {
    try {
      setLoading(true);
      const response = await getBroadCasts({
        page: currentPage,
        perPage: PER_PAGE,
      });
  
      const data = response.data;
      setApiTotal(data.total);
  
      const message = data.messagingLimitTier;
      // In fetchData function, make sure these lines exist:
const numericLimit = message ? message.split("_")[1] : "";
setNumericMessagingLimit(numericLimit);  // This sets the WhatsApp limit
setDailyMailTracker(data.dailyMailTracker || []);  // This sets email limits
setSevenDayOrgStats(data.sevenDayOrgStats || {});  // This sets 7-day metrics
setThirtyDayOrgStats(data.thirtyDayOrgStats || {});  // This sets 30-day metrics
logger.log('📊 Metrics data:', { sevenDay: data.sevenDayOrgStats, thirtyDay: data.thirtyDayOrgStats });
  
      const transformedData: CampaignItem[] = data.data?.map((ele: any) => ({
        key: ele._id,
        id: ele._id,
        name: ele.name,
        scheduleAt: ele.scheduleAt
          ? moment.utc(ele.scheduleAt).local().format("Do MMM YY HH:mm A")
          : "-",
        rawScheduleAt: ele.scheduleAt,
        totalQueued: ele.totalQueued,
        totalProcessed: ele.totalProcessed,
        type: ele.serviceType,
        createdBy: ele.createdBy,
        status: ele.status,
      })) || [];
  
      setAllCampaigns(transformedData);
      
      const filtered = transformedData.filter((item: CampaignItem) => {
        if (!item.rawScheduleAt) return activeTab === 'current';
        const scheduleTime = moment.utc(item.rawScheduleAt).local();
        return activeTab === 'current' 
          ? scheduleTime.isSameOrBefore(now) 
          : scheduleTime.isAfter(now);
      });
      setDisplayData(filtered);
    } catch (error) {
      console.error('Error fetching broadcasts:', error);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    fetchData(1);
  }, []);

  // Refresh when navigating back from campaign creation
  useEffect(() => {
    if (location.state?.refresh) {
      logger.log('🔄 Refreshing campaigns after create/update');
      fetchData(page);
      // Clear the state to prevent repeated refreshes
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.state]);

  useEffect(() => {
    if (allCampaigns.length > 0) {
      const now = moment();
      const filtered = allCampaigns.filter((item: CampaignItem) => {
        if (!item.rawScheduleAt) return activeTab === 'current';
        const scheduleTime = moment.utc(item.rawScheduleAt).local();
        return activeTab === 'current' 
          ? scheduleTime.isSameOrBefore(now) 
          : scheduleTime.isAfter(now);
      });
      setDisplayData(filtered);
    }
  }, [activeTab, allCampaigns]);
  
  const formatLastActivity = (lastActivity: string, lastActivityType: string) => {
    if (!lastActivity) return 'No activity';
    return `${lastActivityType || 'Activity'} ${moment(lastActivity).fromNow()}`;
  };

  const onRowClicked = (record: any) => {
    if (record.id) {
      setSelectedCampaignId(record.id);
      setIsDetail(true);
    }
  };


  if (isUltimate === false) { // Explicitly check for false
    return (
      <div id="main" className="main">
        <Header />
        <UltimatePlanInfo />
      </div>
    );
  }
  
  return (
    <>
      <div id="main" className="main px-2">
        <Header />
        {!isDetail ? (
          <div className="whatsapp-broadcast" style={{ padding: "0 15px" }}>
            <section className="auto-section-1 px-0">
              <div className="d-flex align-items-center justify-content-between w-100">
                <div className="align-items-center auto-section-1-sub-1">
                  <label>Campaigns ({apiTotal})</label>
                </div>
                <div className="d-flex align-items-center ms-auto">
                 <LimitsDisplay
  dailyMailTracker={dailyMailTracker}
  whatsappLimit={numericMessagingLimit}
/>
                  <button
                    onClick={() => {
                      fetchData(1);
                      toast.success('Campaigns refreshed');
                    }}
                    className="btn fw-bold help-btn me-2"
                    style={{
                      fontSize: 14,
                      background: '#6366F1',
                      color: 'white',
                      border: '1px solid #6366F1',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px'
                    }}
                  >
                    ↻ Refresh
                  </button>
                  <NavLink
                    className="btn fw-bold auto_btn w-auto"
                    to="/createWhatsappBroadcast"
                    style={{ fontSize: 14 }}
                  >
                    <Send size={16} color="white" className="me-2" />
                    New Campaign
                  </NavLink>
                </div>
              </div>
            </section>

            <MetricsDisplay
              sevenDayOrgStats={sevenDayOrgStats}
              thirtyDayOrgStats={thirtyDayOrgStats}
            />

            <div className="campaign-table-container">
              <Tabs 
                activeKey={activeTab} 
                onChange={setActiveTab}
                className="campaign-tabs"
              >
                <TabPane tab="Current Campaigns" key="current" />
                <TabPane tab="Scheduled Campaigns" key="scheduled" />
              </Tabs>

              <Table 
  columns={columns}
  dataSource={displayData}
  loading={loading}
  pagination={
    activeTab === 'current' ? {
      total: apiTotal,
      pageSize: PER_PAGE,
      current: page,
      onChange: (newPage) => {
        setPage(newPage);
        fetchData(newPage);
      },
      showSizeChanger: false,
      position: ['bottomCenter'],
      showTotal: (totalItems: number) => `Total ${totalItems} items`,
    } : false
  }
  onRow={(record) => ({
    onClick: () => onRowClicked(record),
  })}
  scroll={{ 
    y: 'calc(100vh - 300px)',
    scrollToFirstRowOnChange: true
  }}
  className="campaigns-table"
  locale={{
    emptyText: <EmptyState activeTab={activeTab} />,
  }}
/>
            </div>
            </div>
        ) : (
          selectedCampaignId && (
            <CampaignView
              campaignId={selectedCampaignId}
              onClose={() => {
                setSelectedCampaignId(null);
                setIsDetail(false);
                fetchData(page); // Refresh campaign list when closing detail view
              }}
            />
          )
        )}
      </div>
    </>
  );
};

export default WhatsappBroadcast;